import React, { useState, MouseEvent } from 'react';
import { ClassValue } from 'classnames/types';
import CollapsibleBlockContent from './CollapsibleBlockContent/CollapsibleBlockContent';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Icon from '../Icon/Icon';
import BodyText from '../Typography/BodyText/BodyText';
import style from './CollapsibleBlock.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import Gutter from '../Gutter/Gutter';

type CollapsibleBlockProps = {
  /** Visible heading */
  blockTitle: string;
  /** Controls if initially expanded or not */
  expanded?: boolean;
  /** Class name */
  className?: ClassValue;
  /** Content to be shown when expanded */
  children: NonNullable<React.ReactNode>;
  /** Background color  */
  backgroundColor?: BackgroundColor;
  /** Optional Number of items  */
  numberOfItems?: number;
};

const CollapsibleBlock: React.FunctionComponent<CollapsibleBlockProps> & {
  Content: typeof CollapsibleBlockContent;
} = ({
  blockTitle,
  children,
  className,
  backgroundColor = 'white',
  expanded: initiallyExpanded,
  numberOfItems,
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded ?? false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const blur = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const collapsibleBlockClasses = [
    className,
    style.collapsibleBlock,
    'collapsibleBlock-test',
    {
      [style.collapsibleBlock__purpleBackground]:
        backgroundColor === 'purple500',
    },
  ];

  return (
    <Div backgroundColor={backgroundColor} className={collapsibleBlockClasses}>
      <Flex
        className={[style.collapsibleBlock_header]}
        data-testid="collapsibleBlock-header"
        onMouseDown={blur}
        onClick={toggleExpanded}
        tabIndex={0}
        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
          e.key === 'Enter' && toggleExpanded();
        }}
        clickableAppearance
        spaceBetween
        centeredVertically
        padding={{ horizontal: 'sm', vertical: 'xs' }}
      >
        <Div>
          <Flex centeredVertically>
            <BodyText
              tagName="span"
              className={style.collapsibleBlock_label}
              fontWeight="medium"
              size="md"
            >
              {blockTitle}
            </BodyText>

            <Gutter size="sm" />

            {numberOfItems && (
              <Div
                className={style.collapsibleBlock_numberOfItems}
                backgroundColor="purple300"
                fontWeight="medium"
                data-testid="numberOfItems"
              >
                <Flex centered>
                  <BodyText size="sm">{numberOfItems}</BodyText>
                </Flex>
              </Div>
            )}
          </Flex>
        </Div>

        <Icon
          className={style.collapsibleBlock_icon}
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size="md"
          color="black"
        />
      </Flex>

      {expanded && children}
    </Div>
  );
};

CollapsibleBlock.Content = CollapsibleBlockContent;

export const backgroundColors = ['white', 'gray200', 'purple500'] as const;

export type BackgroundColor = ArrayElementType<typeof backgroundColors>;

export default CollapsibleBlock;
